<template>
  <div class="web-logs">
    <!-- Modale de détail d'un log -->
    <v-dialog v-model="isDetailOpen" width="90%" max-width="860">
      <modal-detail
        :log="selectedLog"
        :levelColor="levelColor"
        :statutColor="statutColor"
        :formatTs="formatTs"
        @close="closeDetail"
      ></modal-detail>
    </v-dialog>

    <!-- Barre supérieure : titre + KPI compacts + rafraîchir (une seule ligne) -->
    <div class="wl-topbar">
      <div class="wl-head-left">
        <h1 class="wl-title">Logs Web</h1>
        <div class="wl-sub">Suivi de l'activité de vos différents sites web</div>
      </div>
      <template v-if="filters.site">
        <v-chip class="wl-site-chip" color="#5C6BC0" label @click="changeSite">
          <v-icon start size="16">mdi-web</v-icon>
          {{ filters.site }}
          <v-icon end size="16">mdi-close</v-icon>
        </v-chip>
        <div class="wl-kpis">
          <div class="wl-kpi">
            <span class="wl-kpi-value" style="color:#37474F">{{ stats.total }}</span>
            <span class="wl-kpi-label">Total</span>
          </div>
          <div class="wl-kpi">
            <span class="wl-kpi-value" style="color:#EF5350">{{ stats.errors }}</span>
            <span class="wl-kpi-label">Erreurs</span>
          </div>
          <div class="wl-kpi">
            <span class="wl-kpi-value" style="color:#FB8C00">{{ stats.warnings }}</span>
            <span class="wl-kpi-label">Alertes</span>
          </div>
          <div class="wl-kpi">
            <span class="wl-kpi-value" style="color:#5C6BC0">{{ stats.avgDuration }}<span class="wl-kpi-unit">ms</span></span>
            <span class="wl-kpi-label">Moyenne</span>
          </div>
        </div>
        <v-btn class="wl-refresh" variant="flat" :loading="isLoading" @click="refresh">
          <v-icon start>mdi-refresh</v-icon>
          Rafraîchir
        </v-btn>
      </template>
    </div>

    <!-- Écran de sélection du site (avant tout chargement) -->
    <div v-if="!filters.site" class="wl-gate">
      <v-card class="wl-gate-card" variant="flat">
        <div class="wl-gate-badge">
          <v-icon size="34" color="white">mdi-web</v-icon>
        </div>
        <div class="wl-gate-title">Choisissez un site</div>
        <div class="wl-gate-sub">
          Sélectionnez le site dont vous voulez consulter les logs.
        </div>

        <!-- Chargement des sites -->
        <div v-if="isSitesLoading" class="wl-gate-state">
          <v-progress-circular indeterminate color="#5C6BC0" size="42" width="4"></v-progress-circular>
          <div class="wl-gate-state-text">Récupération des sites…</div>
        </div>

        <!-- Erreur / aucun site -->
        <div v-else-if="sitesError" class="wl-gate-state">
          <v-icon size="40" color="#EF5350">mdi-alert-circle-outline</v-icon>
          <div class="wl-gate-state-text">Impossible de récupérer les sites.</div>
          <v-btn variant="flat" color="#5C6BC0" style="color:white" @click="loadSites">
            <v-icon start>mdi-refresh</v-icon>
            Réessayer
          </v-btn>
        </div>

        <!-- Menu déroulant des sites -->
        <v-select
          v-else
          v-model="filters.site"
          :items="siteList"
          label="Site"
          variant="outlined"
          density="comfortable"
          hide-details
          clearable
          prepend-inner-icon="mdi-web"
          class="wl-gate-select"
          :menu-props="{ maxHeight: 320 }"
          no-data-text="Aucun site disponible"
        ></v-select>
      </v-card>
    </div>

    <!-- Filtres -->
    <v-card v-if="filters.site" class="wl-filters" variant="flat">
      <v-row dense align="center">
        <v-col cols="12" md="6">
          <v-text-field
            v-model="filters.search"
            label="Rechercher (requête, path, user, message…)"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
          ></v-text-field>
        </v-col>
        <v-col cols="6" md="3">
          <v-select
            v-model="filters.niveau"
            label="Niveau"
            :items="levelOptions"
            variant="outlined"
            density="compact"
            hide-details
            multiple
            chips
            closable-chips
          ></v-select>
        </v-col>
        <v-col cols="6" md="3">
          <v-text-field
            v-model="filters.statut"
            label="Statut (ex : 200, 4, 500)"
            variant="outlined"
            density="compact"
            hide-details
            clearable
          ></v-text-field>
        </v-col>

        <v-col cols="12" md="4">
          <v-select
            v-model="filters.site"
            label="Site"
            :items="siteList"
            :loading="isSitesLoading"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            prepend-inner-icon="mdi-web"
            :menu-props="{ maxHeight: 320 }"
          ></v-select>
        </v-col>
        <v-col cols="6" md="4">
          <v-select
            v-model="filters.client"
            label="Client (IP)"
            :items="clientOptions"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            prepend-inner-icon="mdi-ip-network-outline"
          ></v-select>
        </v-col>
        <v-col cols="6" md="4">
          <v-select
            v-model="filters.user"
            label="User"
            :items="userOptions"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            prepend-inner-icon="mdi-account-outline"
          ></v-select>
        </v-col>

        <v-col cols="6" md="3">
          <DatePicker
            v-model="filters.dateFrom"
            :format="'dd/MM/yyyy'"
            :modelType="'timestamp'"
            locale="fr"
            :enableTimePicker="false"
            autoApply
            placeholder="Date début"
          ></DatePicker>
        </v-col>
        <v-col cols="6" md="3">
          <DatePicker
            v-model="filters.dateTo"
            :format="'dd/MM/yyyy'"
            :modelType="'timestamp'"
            locale="fr"
            :enableTimePicker="false"
            autoApply
            placeholder="Date fin"
          ></DatePicker>
        </v-col>
        <v-col cols="12" md="6" class="d-flex align-center wl-filter-actions">
          <v-btn variant="text" color="#607D8B" @click="resetFilters">
            <v-icon start>mdi-filter-remove-outline</v-icon>
            Réinitialiser
          </v-btn>
          <v-spacer></v-spacer>
          <v-switch
            v-model="autoRefresh"
            color="#5C6BC0"
            hide-details
            density="compact"
            label="Auto (30s)"
          ></v-switch>
        </v-col>
      </v-row>
    </v-card>

    <!-- Écran de chargement des logs -->
    <div v-if="filters.site && isLoading" class="wl-loading">
      <v-progress-circular indeterminate color="#5C6BC0" size="64" width="5"></v-progress-circular>
      <div class="wl-loading-title">Chargement des logs…</div>
      <div class="wl-loading-sub">Site : {{ filters.site }}</div>
    </div>

    <!-- Tableau -->
    <v-card v-if="filters.site && !isLoading" ref="tableCard" class="wl-table-card" variant="flat">
      <EasyDataTable
        table-class-name="wl-table"
        :headers="displayHeaders"
        :items="filteredLogs"
        :sort-by="'horodatage'"
        :sort-type="'desc'"
        :rows-per-page="25"
        :table-height="tableHeight"
        rows-per-page-message="Lignes par page"
        rows-of-page-separator-message="sur"
        empty-message="Aucun log à afficher"
        @click-row="openDetail"
      >
        <template #item-horodatage="item">
          <span class="wl-cell-date">{{ formatTs(item.horodatage) }}</span>
        </template>

        <template #item-niveau="item">
          <v-chip
            size="small"
            :style="{ backgroundColor: levelColor(item.niveau), color: 'white', fontWeight: 700 }"
          >
            {{ item.niveau || "—" }}
          </v-chip>
        </template>

        <template #item-statut="item">
          <v-chip
            v-if="item.statut"
            size="small"
            :style="{ backgroundColor: statutColor(item.statut), color: 'white', fontWeight: 700 }"
          >
            {{ item.statut }}
          </v-chip>
          <span v-else>—</span>
        </template>

        <template #item-request="item">
          <div class="wl-req" :title="item.request">
            <v-chip
              v-if="requestMethod(item.request)"
              size="x-small"
              label
              :style="{ backgroundColor: methodColor(item.request), color: 'white', fontWeight: 700 }"
            >
              {{ requestMethod(item.request) }}
            </v-chip>
            <span class="wl-cell-mono wl-req-path">{{ requestPath(item.request) || "—" }}</span>
          </div>
        </template>

        <template #item-site="item">
          <span class="wl-cell-ellipsis" :title="item.path">{{ siteHost(item.path) || "—" }}</span>
        </template>

        <template #item-client="item">
          <div class="wl-stack" :title="item.client">
            <span class="wl-stack-main">{{ clientIp(item.client) || "—" }}</span>
            <span class="wl-stack-sub">{{ clientAgentShort(item.client) }}</span>
          </div>
        </template>

        <template #item-user="item">
          <span class="wl-cell-ellipsis" :title="item.user">{{ item.user || "—" }}</span>
        </template>

        <template #item-duree_ms="item">
          <span v-if="item.duree_ms">{{ item.duree_ms }} ms</span>
          <span v-else>—</span>
        </template>

        <template #item-actions="item">
          <div class="d-flex align-center justify-end">
            <v-btn variant="text" density="compact" size="small" color="#5C6BC0" @click.stop="openDetail(item)">
              <v-icon size="20">mdi-eye-outline</v-icon>
            </v-btn>
            <v-btn variant="text" density="compact" size="small" color="#EF5350" @click.stop="confirmDelete(item)">
              <v-icon size="20">mdi-delete-outline</v-icon>
            </v-btn>
          </div>
        </template>
      </EasyDataTable>
    </v-card>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" location="top">
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import homePageData from "../hooks/page-home";
import ModalDetail from "./modal-detail.vue";

export default {
  setup() {
    const {
      logs,
      isLoading,
      isSitesLoading,
      sitesError,
      filters,
      siteList,
      siteOptions,
      clientOptions,
      userOptions,
      levelOptions,
      selectedLog,
      isDetailOpen,
      autoRefresh,
      snackbar,
      filteredLogs,
      stats,
      loadLogs,
      refresh,
      resetFilters,
      changeSite,
      openDetail,
      closeDetail,
      deleteLog,
      formatTs,
      levelColor,
      statutColor,
      clientIp,
      clientAgentShort,
      siteHost,
      requestMethod,
      requestPath,
      methodColor,
    } = homePageData();

    return {
      logs,
      isLoading,
      isSitesLoading,
      sitesError,
      filters,
      siteList,
      siteOptions,
      clientOptions,
      userOptions,
      levelOptions,
      selectedLog,
      isDetailOpen,
      autoRefresh,
      snackbar,
      filteredLogs,
      stats,
      loadLogs,
      refresh,
      resetFilters,
      changeSite,
      openDetail,
      closeDetail,
      deleteLog,
      formatTs,
      levelColor,
      statutColor,
      clientIp,
      clientAgentShort,
      siteHost,
      requestMethod,
      requestPath,
      methodColor,
    };
  },
  data() {
    return {
      windowHeight: window.innerHeight,
      windowWidth: window.innerWidth,
      tableHeight: 400,
      autoTimer: null,
      headers: [
        { value: "horodatage", text: "Date", sortable: true, width: 150 },
        { value: "niveau", text: "Niveau", sortable: true, width: 92 },
        { value: "statut", text: "Statut", sortable: true, width: 88 },
        { value: "request", text: "Requête", sortable: true, width: 230 },
        { value: "site", text: "Site", sortable: false, width: 165 },
        { value: "client", text: "Client", sortable: false, width: 185 },
        { value: "user", text: "User", sortable: false, width: 170 },
        { value: "duree_ms", text: "Durée", sortable: true, width: 92 },
        { value: "actions", text: "", sortable: false, width: 84 },
      ],
    };
  },
  computed: {
    isSmallScreen() {
      return this.$vuetify.display.mdAndDown;
    },
    // Sur petit écran on ne garde que l'essentiel pour éviter le scroll
    // horizontal ; le détail complet reste accessible via l'œil.
    displayHeaders() {
      if (this.isSmallScreen) {
        const keep = ["horodatage", "niveau", "statut", "request", "actions"];
        return this.headers.filter((h) => keep.includes(h.value));
      }
      return this.headers;
    },
  },
  watch: {
    autoRefresh(val) {
      this.setupAutoRefresh(val);
    },
  },
  mounted() {
    window.addEventListener("resize", this.onResize);
    this.computeTableHeight();
    // Recalcule après la mise en page finale (polices, filtres qui se replient…)
    setTimeout(this.computeTableHeight, 150);
    setTimeout(this.computeTableHeight, 500);
  },
  updated() {
    // La hauteur des filtres peut changer (chips de niveau, retour à la ligne…)
    this.computeTableHeight();
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.onResize);
    if (this.autoTimer) clearInterval(this.autoTimer);
  },
  methods: {
    onResize() {
      this.windowHeight = window.innerHeight;
      this.windowWidth = window.innerWidth;
      this.computeTableHeight();
    },
    // Ajuste la hauteur du tableau pour que la carte remplisse l'espace : la
    // marge sous la carte doit être égale à la marge à gauche/droite.
    // Méthode auto-correctrice : on mesure l'écart entre le bas voulu et le bas
    // réel de la carte, puis on ajuste la hauteur d'autant (mapping 1:1).
    computeTableHeight() {
      this.$nextTick(() => {
        var comp = this.$refs.tableCard;
        var el = comp && comp.$el ? comp.$el : comp;
        if (!el || !el.getBoundingClientRect) return;
        var rect = el.getBoundingClientRect();
        var sideGap = Math.round(rect.left); // marge gauche = marge voulue en bas
        var desiredBottom = window.innerHeight - sideGap;
        var delta = Math.floor(desiredBottom - rect.bottom);
        if (Math.abs(delta) <= 1) return; // déjà stable → on arrête (pas de boucle)
        var h = this.tableHeight + delta;
        if (h < 200) h = 200;
        this.tableHeight = h;
      });
    },
    setupAutoRefresh(enabled) {
      if (this.autoTimer) {
        clearInterval(this.autoTimer);
        this.autoTimer = null;
      }
      if (enabled) {
        this.autoTimer = setInterval(() => {
          this.refresh();
        }, 30000);
      }
    },
    confirmDelete(item) {
      // Suppression directe (le back confirmera). On garde un garde-fou simple.
      this.deleteLog(item);
    },
  },
  components: {
    ModalDetail,
  },
};
</script>

<style>
@import "../style.css";
</style>
